import { useCallback, useMemo, useState } from "react";

import { getFirstDayOfMonth } from "@/lib/dayjs/get-first-day-of-month";
import { getLastDayOfMonth } from "@/lib/dayjs/get-last-day-of-month";
import { MONTHS, getYearRange } from "@/lib/dayjs/get-year-range";

/**
 * 年月の最初の日を返す
 *
 * @param year 年
 * @param month 月
 * @returns 年月の最初の日
 *
 * @example
 * const date = getFirstDayOfYearAndMonth({ year: "2024", month: "01" });
 * console.log(date); // 2024-01-01
 */
const getFirstDayOfYearAndMonth = ({ year, month }: { year: string; month: string }) => {
	return `${year}-${month}-01`;
};

type Props = {
	/**
	 * YYYY-MM-DD の形式
	 */
	initialDt: string;
};

export const useYearAndMonth = ({ initialDt }: Props) => {
	const [thisYear, thisMonth] = initialDt.split("-");
	const [year, setYear] = useState(thisYear);

	// ゼロサプレス後の月
	const _thisMonth = String(Number(thisMonth));
	const [month, setMonth] = useState(_thisMonth);

	const onSetYear = useCallback((input: string) => {
		setYear(input);
	}, []);

	const onSetMonth = useCallback((input: string) => {
		setMonth(input);
	}, []);

	const onGetStartAndEndDt = useCallback(({ year, month }: { year: string; month: string }) => {
		return {
			startDt: getFirstDayOfMonth({ date: getFirstDayOfYearAndMonth({ year, month }) }),
			endDt: getLastDayOfMonth({ date: getFirstDayOfYearAndMonth({ year, month }) }),
		};
	}, []);

	const reset = useCallback(() => {
		setYear(thisYear);
		setMonth(thisMonth);
	}, [thisYear, thisMonth]);

	const yearOptions = useMemo(() => getYearRange(), []);
	const monthOptions = useMemo(() => MONTHS, []);

	return {
		year,
		onSetYear,
		month,
		onSetMonth,

		reset,

		yearOptions,
		monthOptions,

		onGetStartAndEndDt,
	};
};
